import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useCurrency } from '@/App';
import { RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function SettingsPage() {
  const [exchangeRate, setExchangeRate] = useState('');
  const [autoMode, setAutoMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setExchangeRate: updateGlobalRate, fetchExchangeRate } = useCurrency();

  useEffect(() => {
    fetchCurrentRate();
  }, []);

  const fetchCurrentRate = async () => {
    try {
      const response = await axios.get(`${API}/exchange-rate/`);
      setExchangeRate(response.data.usd_to_pyg.toString());
      setAutoMode(response.data.source === 'auto');
    } catch (error) {
      toast.error('Error al cargar tipo de cambio');
    }
  };

  const handleManualUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/exchange-rate/`, {
        usd_to_pyg: parseFloat(exchangeRate),
        source: 'manual'
      });

      updateGlobalRate(parseFloat(exchangeRate));
      toast.success('Tipo de cambio actualizado exitosamente');
      setAutoMode(false);
    } catch (error) {
      toast.error('Error al actualizar tipo de cambio');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoUpdate = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API}/exchange-rate/auto`);
      setExchangeRate(response.data.usd_to_pyg.toString());
      updateGlobalRate(response.data.usd_to_pyg);
      toast.success('Tipo de cambio obtenido de la API exitosamente');
      setAutoMode(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al obtener tipo de cambio automático');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold" data-font="display">Configuración</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Exchange Rate Settings */}
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Tipo de Cambio (USD → PYG)</CardTitle>
            <CardDescription>
              Configure el tipo de cambio de moneda para soporte multi-moneda
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="auto-mode" className="font-medium">Actualizaciones Automáticas</Label>
                <p className="text-sm text-muted-foreground">Obtener tipo de cambio de API externa</p>
              </div>
              <Switch
                id="auto-mode"
                checked={autoMode}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleAutoUpdate();
                  } else {
                    setAutoMode(false);
                  }
                }}
              />
            </div>

            <form onSubmit={handleManualUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rate">Tipo de Cambio Actual</Label>
                <div className="flex gap-2">
                  <Input
                    id="rate"
                    type="number"
                    step="0.01"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    placeholder="7300"
                    required
                    disabled={autoMode}
                    data-testid="fx-rate-input"
                  />
                  {autoMode && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAutoUpdate}
                      disabled={loading}
                    >
                      <RefreshCw className="size-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  1 USD = {exchangeRate || '0'} PYG
                </p>
              </div>

              {!autoMode && (
                <Button
                  type="submit"
                  className="w-full bg-[hsl(var(--primary))] hover:bg-[#BFA774]"
                  disabled={loading}
                  data-testid="fx-apply-button"
                >
                  {loading ? 'Actualizando...' : 'Actualizar Tipo de Cambio'}
                </Button>
              )}
            </form>

            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                <strong>Modo Manual:</strong> Establecer tipo de cambio manualmente<br />
                <strong>Modo Automático:</strong> Obtener último tipo de cambio de exchangerate-api.com
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Info */}
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Información del Sistema</CardTitle>
            <CardDescription>
              Detalles de la aplicación y configuración
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Aplicación</span>
              <span className="text-sm font-medium">Aurelia Scent OS</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Versión</span>
              <span className="text-sm font-medium">1.0.0</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Soporte de Monedas</span>
              <span className="text-sm font-medium">USD, PYG</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Funcionalidades</span>
              <span className="text-sm font-medium">Ventas, Inventario, Reportes</span>
            </div>
          </CardContent>
        </Card>

        {/* --- INSTALLMENT RULES SETTINGS --- */}
        <InstallmentRulesSettings />

        {/* --- SALES CHANNELS SETTINGS --- */}
        <SalesChannelsSettings />

        {/* --- STOCK IMPORT --- */}
        <StockImportCard />

      </div>
    </div>
  );
}

function StockImportCard() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && !selected.name.match(/\.xlsx?$/i)) {
      toast.error("Solo se permiten archivos .xlsx o .xls");
      e.target.value = "";
      return;
    }
    setFile(selected);
    setResult(null);
    setError(null);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Seleccioná un archivo Excel primero");
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/products/import-stock`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      toast.success("Importación completada");
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al importar el archivo";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle data-font="display">Importar Stock desde Excel</CardTitle>
        <CardDescription>
          Subí un archivo .xlsx o .xls con columnas SKU, Stock Real, Precio y/o Estado para actualizar productos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4 items-end">
          <div className="space-y-2 flex-1">
            <Label htmlFor="stock-file">Archivo Excel</Label>
            <Input
              id="stock-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </div>
          <Button
            onClick={handleImport}
            disabled={!file || loading}
            className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]"
          >
            {loading ? "Importando..." : "Importar Stock"}
          </Button>
        </div>

        {result && (
          <div className="border rounded-lg p-4 space-y-2">
            <h4 className="font-semibold">Resultado</h4>
            <p>Total filas: <strong>{result.total}</strong></p>
            <p>Actualizados: <strong className="text-green-600">{result.updated}</strong></p>
            {result.not_found?.length > 0 && (
              <div>
                <p className="text-amber-600 font-medium">No encontrados ({result.not_found.length}):</p>
                <p className="text-sm text-muted-foreground">{result.not_found.join(", ")}</p>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div>
                <p className="text-red-600 font-medium">Errores ({result.errors.length}):</p>
                <ul className="text-sm text-muted-foreground list-disc list-inside">
                  {result.errors.map((e, i) => (
                    <li key={i}>SKU {e.sku}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="border border-red-300 rounded-lg p-4 text-red-700 bg-red-50">
            {error}
          </div>
        )}

        <div className="pt-4 border-t text-sm text-muted-foreground">
          <p className="font-medium mb-1">Columnas reconocidas (flexibles):</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li><strong>SKU</strong>: sku, código, cod</li>
            <li><strong>Stock Real</strong>: stock real, stock, cantidad, quantity</li>
            <li><strong>Precio (PYG)</strong>: precio, price, price_pyg</li>
            <li><strong>Estado</strong>: estado, status</li>
          </ul>
          <p className="mt-2">Si la columna Estado no se provee, se deriva del stock (0 → sin_stock).</p>
        </div>
      </CardContent>
    </Card>
  );
}

function InstallmentRulesSettings() {
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState({ installments: '', interest_rate: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/installments/`);
      setRules(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddRule = async (e) => {
    e.preventDefault();
    if (!newRule.installments || !newRule.interest_rate) return;
    setLoading(true);
    try {
      await axios.post(`${API}/installments/`, {
        installments: parseInt(newRule.installments),
        interest_rate: parseFloat(newRule.interest_rate)
      });
      toast.success('Regla de cuotas agregada');
      setNewRule({ installments: '', interest_rate: '' });
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar regla');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta regla?')) return;
    try {
      await axios.delete(`${API}/installments/${id}`);
      toast.success('Regla eliminada');
      fetchRules();
    } catch (error) {
      toast.error('Error al eliminar regla');
    }
  };

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle data-font="display">Configuración de Cuotas e Intereses</CardTitle>
        <CardDescription>Defina el interés aplicable según la cantidad de cuotas.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <form onSubmit={handleAddRule} className="flex gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="installments">Cantidad de Cuotas</Label>
              <Input
                id="installments"
                type="number"
                min="1"
                placeholder="Ej: 3"
                value={newRule.installments}
                onChange={(e) => setNewRule({ ...newRule, installments: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="interest">Interés (%)</Label>
              <Input
                id="interest"
                type="number"
                min="0"
                step="0.1"
                placeholder="Ej: 5.0"
                value={newRule.interest_rate}
                onChange={(e) => setNewRule({ ...newRule, interest_rate: e.target.value })}
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
              {loading ? 'Guardando...' : 'Agregar Regla'}
            </Button>
          </form>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Cuotas</th>
                  <th className="px-4 py-2 text-left">Interés</th>
                  <th className="px-4 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rules.sort((a, b) => a.installments - b.installments).map((rule) => (
                  <tr key={rule.id}>
                    <td className="px-4 py-2 font-medium">{rule.installments} Cuotas</td>
                    <td className="px-4 py-2">{rule.interest_rate}%</td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule.id)} className="text-destructive">
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                      No hay reglas configuradas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SalesChannelsSettings() {
  const [channels, setChannels] = useState([]);
  const [newChannel, setNewChannel] = useState({ code: '', name: '', description: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/sales-channels/`);
      setChannels(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddChannel = async (e) => {
    e.preventDefault();
    if (!newChannel.code || !newChannel.name) return;
    setLoading(true);
    try {
      await axios.post(`${API}/sales-channels/`, {
        code: newChannel.code,
        name: newChannel.name,
        description: newChannel.description || null,
      });
      toast.success('Canal agregado');
      setNewChannel({ code: '', name: '', description: '' });
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar canal');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (channel) => {
    try {
      await axios.put(`${API}/sales-channels/${channel.id}`, { is_active: !channel.is_active });
      toast.success('Canal actualizado');
      fetchChannels();
    } catch (error) {
      toast.error('Error al actualizar canal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este canal?')) return;
    try {
      await axios.delete(`${API}/sales-channels/${id}`);
      toast.success('Canal eliminado');
      fetchChannels();
    } catch (error) {
      toast.error('Error al eliminar canal');
    }
  };

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle data-font="display">Canales de Venta</CardTitle>
        <CardDescription>Defina los canales por los que se reciben ventas (local, online, ...).</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <form onSubmit={handleAddChannel} className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="channel-code">Código</Label>
              <Input
                id="channel-code"
                placeholder="Ej: online"
                value={newChannel.code}
                onChange={(e) => setNewChannel({ ...newChannel, code: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="channel-name">Nombre</Label>
              <Input
                id="channel-name"
                placeholder="Ej: Tienda Online"
                value={newChannel.name}
                onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label htmlFor="channel-desc">Descripción</Label>
              <Input
                id="channel-desc"
                placeholder="Opcional"
                value={newChannel.description}
                onChange={(e) => setNewChannel({ ...newChannel, description: e.target.value })}
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
              {loading ? 'Guardando...' : 'Agregar Canal'}
            </Button>
          </form>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Código</th>
                  <th className="px-4 py-2 text-left">Nombre</th>
                  <th className="px-4 py-2 text-left">Descripción</th>
                  <th className="px-4 py-2 text-left">Activo</th>
                  <th className="px-4 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {channels.map((channel) => (
                  <tr key={channel.id}>
                    <td className="px-4 py-2 font-medium">{channel.code}</td>
                    <td className="px-4 py-2">{channel.name}</td>
                    <td className="px-4 py-2 text-muted-foreground">{channel.description || '—'}</td>
                    <td className="px-4 py-2">
                      <Switch checked={channel.is_active} onCheckedChange={() => handleToggle(channel)} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(channel.id)} className="text-destructive">
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))}
                {channels.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No hay canales configurados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// End of SettingsPage
const _unused = null;
